// Pure validation for the customer order form. No React/DOM/network — shared by
// the order form (webpack) and the Node test suite (CommonJS).
//
// Replaces the old blocking alert() checks: returns per-field messages so the UI
// can render inline errors, highlight the fields, and focus the first invalid one.

const { validateEmail } = require("./authValidation");
const { ZIP_DATA } = require("./pricing");

// Field order = the visual order of the form. The first invalid field in this
// order is the one we scroll/focus to.
const FIELD_ORDER = [
  "clientName",
  "clientEmail",
  "defendantName",
  "defendantAddress",
  "defendantZip",
  "court",
  "docType",
  "files",
  "payment",
  "agreed",
];

const MESSAGES = {
  clientName: "Enter your name or firm.",
  clientEmail: "Enter a valid email address so we can send your confirmation.",
  defendantName: "Enter the defendant / subject's full legal name.",
  defendantAddress: "Enter the service address.",
  zipMissing: "Enter the 5-digit service ZIP code.",
  zipInvalid: "Enter a valid 5-digit ZIP code.",
  zipUnserved: "We don't currently serve this ZIP code. Contact us to discuss options.",
  court: "Select the court jurisdiction.",
  docType: "Select the document type.",
  files: "Upload your court documents (PDF, JPG, or PNG).",
  payment: "Select a payment method.",
  agreed: "Read and agree to the Service Agreement to continue.",
};

function isBlank(v) {
  return typeof v !== "string" || v.trim() === "";
}

// ZIP gets its own specific messaging: missing vs malformed vs outside the
// service area (which otherwise fails silently and returns an opaque error).
function validateZip(zip) {
  if (isBlank(zip)) return MESSAGES.zipMissing;
  if (!/^\d{5}$/.test(String(zip).trim())) return MESSAGES.zipInvalid;
  if (!ZIP_DATA[String(zip).trim()]) return MESSAGES.zipUnserved;
  return null;
}

// values: { clientName, clientEmail, defendantName, defendantAddress,
//           defendantZip, court, payment, files, agreed }
// returns { ok, errors: {field: message}, firstInvalid: field|null }
function validateOrderForm(values) {
  const v = values || {};
  const errors = {};

  if (isBlank(v.clientName)) errors.clientName = MESSAGES.clientName;
  if (!validateEmail(v.clientEmail)) errors.clientEmail = MESSAGES.clientEmail;
  if (isBlank(v.defendantName)) errors.defendantName = MESSAGES.defendantName;
  if (isBlank(v.defendantAddress)) errors.defendantAddress = MESSAGES.defendantAddress;

  const zipError = validateZip(v.defendantZip);
  if (zipError) errors.defendantZip = zipError;

  if (isBlank(v.court)) errors.court = MESSAGES.court;
  // Document type is REQUIRED. A blank type can never be auto-priced, so it must
  // not reach pricing, submission or payment.
  if (isBlank(v.docType)) errors.docType = MESSAGES.docType;
  if (!Array.isArray(v.files) || v.files.length === 0) errors.files = MESSAGES.files;
  // A review-required type ("Other") has no price and no payment step, so a
  // payment method is neither collected nor required for it.
  if (!v.reviewRequired && isBlank(v.payment)) errors.payment = MESSAGES.payment;
  if (!v.agreed) errors.agreed = MESSAGES.agreed;

  const firstInvalid = FIELD_ORDER.find((f) => errors[f]) || null;
  return { ok: Object.keys(errors).length === 0, errors, firstInvalid };
}

module.exports = { FIELD_ORDER, MESSAGES, validateZip, validateOrderForm };
